/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// "Add contact" form modal — a contact-creation form (name + email/phone),
// distinct from the "New message" flow. Mirrors the CreateFolderModal shell
// (transparent Modal / web body portal + backdrop + card + inputs +
// Cancel/Add). The host persists the result (device-contact store on web).

import { X } from "lucide-react-native";
import { useEffect, useState } from "react";
import { Modal, Pressable, StyleSheet, Text, TextInput, View } from "react-native";
import { WEB_INPUT_OUTLINE_RESET } from "../foundations/webInputStyle";
import { WebPortal } from "../chat/composer/WebPortal";

const MAX_NAME = 80;
const MAX_IDENTIFIER = 120;
const IS_WEB = typeof document !== "undefined" && typeof window !== "undefined";

export interface AddContactDraft {
	readonly displayName: string;
	/** Email or phone used to reach / resolve the contact. May be empty. */
	readonly identifier: string;
}

export interface AddContactModalProps {
	readonly open: boolean;
	readonly onDismiss: () => void;
	/** Create the contact with the trimmed fields (displayName is non-empty). */
	readonly onCreate: (draft: AddContactDraft) => void;
}

export function AddContactModal({
	open,
	onDismiss,
	onCreate,
}: AddContactModalProps): JSX.Element | null {
	const [name, setName] = useState("");
	const [identifier, setIdentifier] = useState("");
	useEffect(() => {
		if (!open) {
			setName("");
			setIdentifier("");
		}
	}, [open]);

	if (!open) return null;
	const trimmedName = name.trim();
	const trimmedId = identifier.trim();

	const submit = (): void => {
		if (trimmedName.length === 0) return;
		onCreate({ displayName: trimmedName, identifier: trimmedId });
		onDismiss();
	};

	const card = (
		<Pressable
			accessibilityRole="none"
			onPress={onDismiss}
			style={StyleSheet.absoluteFill}
			className="z-50 flex-1 items-center justify-center px-md bg-black/60"
		>
			<Pressable
				onPress={() => undefined}
				className="w-full max-w-[420px] rounded-[12px] border bg-light-surface-primary border-light-border-secondary dark:bg-[#15171C] dark:border-[#22242A] shadow-2xl"
			>
				{/* Header */}
				<View className="flex flex-row items-center justify-between px-[29px] pt-5 pb-2">
					<Text className="m-0 font-sans font-bold text-[20px] leading-7 text-light-text-primary dark:text-white">
						Add contact
					</Text>
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="Close"
						onPress={onDismiss}
						className="w-[26px] h-[26px] rounded-[8px] flex items-center justify-center hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]"
					>
						<X size={18} strokeWidth={2} color="#B5BAC3" />
					</Pressable>
				</View>

				{/* Inputs */}
				<View className="px-[29px] pt-1 pb-2 gap-3">
					<View className="gap-1">
						<Text className="font-sans text-[14px] leading-5 font-semibold text-light-text-primary dark:text-[#F5F6F8]">
							Name
						</Text>
						<View className="flex flex-row items-center h-[38px] px-3 gap-sm rounded-[8px] bg-light-surface-secondary dark:bg-[#111318]">
							<TextInput
								value={name}
								onChangeText={(t) => setName(t.slice(0, MAX_NAME))}
								placeholder="Contact name"
								placeholderTextColor="#6B7280"
								autoFocus
								maxLength={MAX_NAME}
								className="flex-1 font-sans text-[14px] text-light-text-primary dark:text-white"
								style={WEB_INPUT_OUTLINE_RESET}
							/>
						</View>
					</View>
					<View className="gap-1">
						<Text className="font-sans text-[14px] leading-5 font-semibold text-light-text-primary dark:text-[#F5F6F8]">
							Email or phone
						</Text>
						<View className="flex flex-row items-center h-[38px] px-3 gap-sm rounded-[8px] bg-light-surface-secondary dark:bg-[#111318]">
							<TextInput
								value={identifier}
								onChangeText={(t) => setIdentifier(t.slice(0, MAX_IDENTIFIER))}
								placeholder="name@example.com or +1 555 000 0000"
								placeholderTextColor="#6B7280"
								autoCapitalize="none"
								autoCorrect={false}
								maxLength={MAX_IDENTIFIER}
								onSubmitEditing={submit}
								className="flex-1 font-sans text-[14px] text-light-text-primary dark:text-white"
								style={WEB_INPUT_OUTLINE_RESET}
							/>
						</View>
						<Text className="font-sans text-[12px] leading-4 text-light-text-tertiary dark:text-[#6B7280]">
							Used to reach them on Ping. You can leave this blank.
						</Text>
					</View>
				</View>

				{/* Footer */}
				<View className="flex flex-row items-center gap-3 px-[29px] py-4 border-t border-light-border-secondary dark:border-[#22242A]">
					<Pressable
						accessibilityRole="button"
						onPress={onDismiss}
						className="flex-1 h-9 rounded-[8px] items-center justify-center border border-light-border-secondary dark:border-[#2F333B]"
					>
						<Text className="font-sans text-[14px] font-semibold text-light-text-primary dark:text-[#F5F6F8]">
							Cancel
						</Text>
					</Pressable>
					<Pressable
						accessibilityRole="button"
						accessibilityState={{ disabled: trimmedName.length === 0 }}
						disabled={trimmedName.length === 0}
						onPress={submit}
						className={`flex-1 h-9 rounded-[8px] items-center justify-center bg-light-accent-brand dark:bg-dark-accent-brand ${
							trimmedName.length === 0 ? "opacity-40" : "active:opacity-80"
						}`}
					>
						<Text className="font-sans text-[14px] font-bold text-white">Add contact</Text>
					</Pressable>
				</View>
			</Pressable>
		</Pressable>
	);

	if (IS_WEB) {
		return (
			<WebPortal>
				<View style={StyleSheet.absoluteFill}>{card}</View>
			</WebPortal>
		);
	}
	return (
		<Modal visible={open} transparent animationType="none" onRequestClose={onDismiss}>
			{card}
		</Modal>
	);
}
